import { PROGRESS_REPORT_INTERVAL_MS } from '@apm/contracts';
import type { ProgressReportReason, ProgressReportRequest } from '@apm/contracts';

/**
 * 무엇을 언제 보고할지 정하는 **순수 함수** (v16 P7, 특허 청구항 6).
 *
 * **DOM 과 떨어져 있어야 테스트가 가능하다.** 이 저장소에는 jsdom 이 없어 컴포넌트를
 * 렌더해서 재는 방법이 없고, `routing.ts`·`channel-selection.ts`·`playback-clock.ts` 가
 * 같은 이유로 이렇게 떨어져 있다.
 *
 * **특허가 요구하는 것은 「주기 + 중단 이벤트」 둘**이고, 나머지 넷(`skip`·`select`·
 * `channel_switch`·`teardown`)은 그 중단이 무엇이었는지를 가른다.
 */
export interface PlaybackSnapshot {
  /** 일반 채널이 아니면 보고하지 않는다 — 홈쇼핑은 이어보지 않는다 (`docs/38` D5) */
  /** 이력을 갖는 채널인가 — `receiver` 채널 둘 다(일반 · 홈쇼핑 · 2026-09-10). ONAIR 만 아니다 */
  tracksProgress: boolean;
  channelId: string;
  productId: string | null;
  /** 지금 회차 안에서 몇 초 (`receiver.getElapsedSec()`) */
  elapsedSec: number;
  /** 이 회차의 길이. 0 이면 아직 모른다. **보고에는 싣지 않는다**(v18) */
  durationSec: number;
  /**
   * 로그인했는가 (v18 규칙 3).
   *
   * **비로그인이면 보고 자체를 보내지 않는다.** 서버가 어차피 202 로 버리지만(축이 계정
   * 하나다), 10초마다 버려질 요청을 쏘는 것은 그냥 낭비다. 그리고 **판정이 여기 있어야**
   * node 환경 테스트가 그것을 본다.
   */
  loggedIn: boolean;
  /**
   * 이 화면이 지금 보이는가 (v18, `document.visibilityState === 'visible'`).
   *
   * **숨은 동안에는 주기 보고를 보내지 않는다.** 데스크톱은 탭이 숨어도 영상을 계속
   * 돌리므로, 막지 않으면 **뒤에 열어 둔 탭이 10초마다 지점을 덮어쓴다** — 손에 든 폰으로
   * 보고 있는데 지점은 노트북이 정하는 상태가 되고, 그 폰이 돌아왔을 때
   * (`resume-return.ts`) 받아 오는 값도 그 덮어쓴 값이다.
   *
   * **앞에 있는 기기가 이긴다**가 한 지점 모델에서 유일하게 설명 가능한 규칙이다.
   *
   * **중단 보고(`eventReport`)는 막지 않는다** — `teardown` 은 정확히 숨는 순간에 나가는
   * 것이고, 그것이 막히면 **떠나기 직전 10초가 늘 사라진다.**
   */
  visible: boolean;
  deviceId: string;
  /** 단조 증가하는 시계. 테스트가 값을 준다 */
  nowMs: number;
}

export interface ReportState {
  /** 마지막으로 보고한 시각. 아직 없으면 null */
  lastAtMs: number | null;
  /** 마지막으로 보고한 (회차, 초). 같은 값을 두 번 보내지 않으려고 붙든다 */
  lastProductId: string | null;
  lastPositionSec: number;
}

export const INITIAL_REPORT_STATE: ReportState = {
  lastAtMs: null,
  lastProductId: null,
  lastPositionSec: 0,
};

/**
 * 주기 보고. **보낼 것이 없으면 `null`** 이다.
 *
 * 보내지 않는 경우가 여섯이다 — 일반 채널이 아니거나 · 로그인하지 않았거나 · 재생 중인
 * 회차가 없거나 · **화면이 숨어 있거나** · 주기가 아직 안 됐거나 · **같은 자리에 머물러
 * 있거나.** 끝의 것은 일시정지·버퍼링에서 같은 값을 10초마다 쓰는 것을 막는다.
 */
export function nextIntervalReport(
  prev: ReportState,
  now: PlaybackSnapshot,
): ProgressReportRequest | null {
  if (!now.tracksProgress || !now.loggedIn || now.productId === null) return null;
  // 숨은 동안에는 쓰지 않는다 — 위 `visible` 주석이 이유다.
  if (!now.visible) return null;

  const due = prev.lastAtMs === null || now.nowMs - prev.lastAtMs >= PROGRESS_REPORT_INTERVAL_MS;
  if (!due) return null;

  const moved =
    now.productId !== prev.lastProductId || Math.floor(now.elapsedSec) !== prev.lastPositionSec;
  if (!moved) return null;

  return buildReport(now, 'interval');
}

/**
 * 사건 보고 — 주기와 무관하게 즉시 보낸다.
 *
 * **`select` 만 서버에서 되감기가 허용된다** (`docs/38` §4.3). 그래서 목록에서 고른 뒤의
 * 보고는 반드시 이 사유여야 한다 — `interval` 로 보내면 「처음부터 다시 보기」가 단조 증가
 * 규칙에 막혀 영원히 안 된다.
 *
 * ── ★ v33 — `interval` 을 막던 타입을 풀었다 ─────────────────
 *
 * 그 `Exclude` 는 **「주기 보고는 `nextIntervalReport` 를 쓰라」는 안내**였지 계약이
 * 아니었다. 라이브 바이패스의 **진입 보고**는 「사건」이지만 사유는 `interval` 이어야
 * 한다 — `PROGRESS_REPORT_REASONS` 를 늘리지 않기로 했고(`docs/67` D7), 컷 지점은 언제나
 * 직전 보고보다 크거나 같아 **단조 증가 규칙이 그대로 받는다.**
 *
 * **함수 이름은 「언제 보내는가」이고 `reason` 은 「무엇이었는가」다** — 둘이 갈리는 것이
 * 이 한 자리이고, 그래서 주기 보고의 입구는 여전히 `nextIntervalReport` 하나다.
 */
export function eventReport(
  now: PlaybackSnapshot,
  reason: ProgressReportReason,
): ProgressReportRequest | null {
  if (!now.tracksProgress || !now.loggedIn || now.productId === null) return null;
  return buildReport(now, reason);
}

/** 보고를 보낸 뒤의 상태. **보내지 못했어도 갱신한다** — 실패를 10초마다 재시도하지 않는다 */
export function afterReport(now: PlaybackSnapshot): ReportState {
  return {
    lastAtMs: now.nowMs,
    lastProductId: now.productId,
    lastPositionSec: Math.floor(now.elapsedSec),
  };
}

function buildReport(
  now: PlaybackSnapshot,
  reason: ProgressReportReason,
): ProgressReportRequest | null {
  if (now.productId === null) return null;
  return {
    deviceId: now.deviceId,
    channelId: now.channelId,
    items: [
      {
        productId: now.productId,
        // 서버는 정수를 받는다. **내림이다** — 올리면 아직 안 본 구간을 본 것으로 만든다.
        positionSec: Math.max(0, Math.floor(now.elapsedSec)),
      },
    ],
    reason,
  };
}

/**
 * ★ **회차 사이 브레이크가 열렸다 — 다음 회차를 「이어볼 회차」로 올릴 것인가** (v28).
 *
 * ── 무엇을 고치는가 ─────────────────────────────────────────
 *
 * 진행이 **「끝낸 회차의 끝」에 머무는 창**이 있었다. 최대 50초다 — 그 회차의 마지막
 * 주기 보고(≤10초) + **회차 사이 브레이크 30초**(그동안 주기 보고가 막혀 있다 · v21 이
 * 옳게 막았다) + 다음 회차의 첫 주기 보고(≤10초).
 *
 * 그 창에서 채널을 옮겼다 오면 **끝낸 회차의 끝 + 회차 사이 광고를 다시** 본다.
 * 2026-09-05 무대에서 그렇게 신고됐다 — `complete@240` 3초 뒤 재진입이 **@237** 이었고
 * 22초 뒤 또 **@239** 였다(`docs/58` §7).
 *
 * ── ★ 왜 화면이 다음 회차를 직접 집는가 ─────────────────────
 *
 * 브레이크 동안 리시버의 `index` 는 **아직 끝난 회차**다 — `advance` 는 브레이크가
 * **닫힐 때** 소비된다(`closeAdBreak` → `advanceContent`). 그래서 `progressSnapshot()`
 * 이 다음 회차를 가리킬 수 없다.
 *
 * ── 판정 넷 ─────────────────────────────────────────────────
 *
 * ① 회차 사이 브레이크일 때만. **미드롤은 아니다** — 그때는 회차가 안 끝났다
 * ② **브레이크 하나에 한 번만** (`reportedFor`) — 광고가 넘어갈 때마다 다시 부르지 않는다
 * ③ **회차가 하나뿐이면 `null`** — 다음이 곧 자기 자신이라 **되감기가 된다.**
 *    서버의 `Math.max` 가 흡수하기는 하지만(같은 회차다), 그 흡수에 기대면
 *    **`reason` 처리를 바꾸는 날 조용히 되감긴다**
 * ④ 목록이 비었거나 지금 회차를 모르면 `null`
 *
 * @returns 보고할 다음 회차의 `productId`. 보내지 않을 자리면 `null`
 */
export function advanceOnBreak(input: {
  /** 지금 열려 있는 브레이크의 종류. 없으면 `null` */
  breakKind: string | null;
  /** 지금(= 끝나 가는) 회차 */
  currentProductId: string | null;
  items: readonly { productId: string }[];
  index: number;
  /** 이 브레이크에서 이미 보고했는가 — 그 회차의 `productId` 를 들고 있다 */
  reportedFor: string | null;
}): string | null {
  const { breakKind, currentProductId, items, index, reportedFor } = input;
  if (breakKind !== 'between_episodes') return null;
  if (currentProductId === null) return null;
  if (reportedFor === currentProductId) return null;
  if (items.length === 0) return null;
  const next = items[(index + 1) % items.length];
  if (next === undefined || next.productId === currentProductId) return null;
  return next.productId;
}
